import type { InstitutionalProfileFormData } from '@/types/institutionalProfile'

/**
 * The institutional profile as *data*, not as JSX.
 *
 * The previous implementation spelled these ~70 fields out longhand in a 690-line
 * desktop component plus a separate 372-line mobile one — two places to forget a
 * field, no dark mode, and no way to point a server-side validation error at a
 * control. Declaring the fields once and rendering them from a single component
 * makes "mark `sanctions_compliance` invalid" a lookup instead of a search.
 */

export type FieldKind = 'text' | 'email' | 'tel' | 'date' | 'textarea' | 'select' | 'checkbox'

export interface SelectOption {
  value: string
  label: string
}

export interface FieldDef {
  name: keyof InstitutionalProfileFormData
  label: string
  kind: FieldKind
  placeholder?: string
  options?: SelectOption[]
  /** Rendered under the control, always visible — for context, not for errors. */
  help?: string
  /** Columns to occupy in the 2-column desktop grid. Mobile is always 1. */
  span?: 1 | 2
  /**
   * Accepted on write but never returned by the API. On resume the value comes
   * back empty with a `<field>_set` boolean instead, so an empty value means
   * "unchanged", never "cleared".
   */
  writeOnly?: boolean
  /**
   * Render only while the named field's current value is one of `equalsAny`.
   * Used for the incorporation state/province selects, which only make sense
   * for the two countries whose registry checks require a subdivision code.
   */
  showWhen?: { field: keyof InstitutionalProfileFormData; equalsAny: readonly string[] }
}

export interface SectionDef {
  id: string
  title: string
  description?: string
  fields: FieldDef[]
}

const INSTITUTION_TYPES: SelectOption[] = [
  { value: 'investment_adviser', label: 'Investment Adviser' },
  { value: 'broker_dealer', label: 'Broker-Dealer' },
  { value: 'bank', label: 'Bank' },
  { value: 'insurance_company', label: 'Insurance Company' },
  { value: 'pension_fund', label: 'Pension Fund' },
  { value: 'endowment', label: 'Endowment' },
  { value: 'foundation', label: 'Foundation' },
  { value: 'family_office', label: 'Family Office' },
  { value: 'hedge_fund', label: 'Hedge Fund' },
  { value: 'private_equity', label: 'Private Equity' },
  { value: 'venture_capital', label: 'Venture Capital' },
  { value: 'other', label: 'Other' },
]

/**
 * The seven legal forms, exported because the KYB panels reuse them.
 *
 * 🔴 The values are a CONTRACT with the backend: each maps 1:1 onto a Sumsub
 * company verification level, resolved SERVER-SIDE (`LEVEL_BY_LEGAL_FORM` in
 * alocasia `core/sumsub/levels.py`). The client never sends a level name — a
 * client that could pick the level could pick a weaker one than the entity
 * warrants. Do not add, remove, or rename a value without the backend pairing.
 * `institution_type` above is an INDUSTRY taxonomy and is unrelated.
 */
export const LEGAL_FORMS: SelectOption[] = [
  { value: 'soleprop', label: 'Sole proprietorship' },
  { value: 'estate', label: 'Estate' },
  { value: 'trust', label: 'Trust' },
  { value: 'organization', label: 'Other organization / nonprofit' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'llc', label: 'LLC' },
  { value: 'corporation', label: 'Corporation' },
]

/**
 * ISO 3166-1 alpha-3 — the code set Sumsub's registry checks key on.
 * United States first for reach; the rest alphabetical by label.
 */
export const INCORPORATION_COUNTRIES: SelectOption[] = [
  { value: 'USA', label: 'United States' },
  { value: 'AFG', label: 'Afghanistan' },
  { value: 'ALB', label: 'Albania' },
  { value: 'DZA', label: 'Algeria' },
  { value: 'AND', label: 'Andorra' },
  { value: 'AGO', label: 'Angola' },
  { value: 'ATG', label: 'Antigua and Barbuda' },
  { value: 'ARG', label: 'Argentina' },
  { value: 'ARM', label: 'Armenia' },
  { value: 'AUS', label: 'Australia' },
  { value: 'AUT', label: 'Austria' },
  { value: 'AZE', label: 'Azerbaijan' },
  { value: 'BHS', label: 'Bahamas' },
  { value: 'BHR', label: 'Bahrain' },
  { value: 'BGD', label: 'Bangladesh' },
  { value: 'BRB', label: 'Barbados' },
  { value: 'BLR', label: 'Belarus' },
  { value: 'BEL', label: 'Belgium' },
  { value: 'BLZ', label: 'Belize' },
  { value: 'BEN', label: 'Benin' },
  { value: 'BMU', label: 'Bermuda' },
  { value: 'BTN', label: 'Bhutan' },
  { value: 'BOL', label: 'Bolivia' },
  { value: 'BIH', label: 'Bosnia and Herzegovina' },
  { value: 'BWA', label: 'Botswana' },
  { value: 'BRA', label: 'Brazil' },
  { value: 'VGB', label: 'British Virgin Islands' },
  { value: 'BRN', label: 'Brunei' },
  { value: 'BGR', label: 'Bulgaria' },
  { value: 'BFA', label: 'Burkina Faso' },
  { value: 'BDI', label: 'Burundi' },
  { value: 'CPV', label: 'Cabo Verde' },
  { value: 'KHM', label: 'Cambodia' },
  { value: 'CMR', label: 'Cameroon' },
  { value: 'CAN', label: 'Canada' },
  { value: 'CYM', label: 'Cayman Islands' },
  { value: 'CAF', label: 'Central African Republic' },
  { value: 'TCD', label: 'Chad' },
  { value: 'CHL', label: 'Chile' },
  { value: 'CHN', label: 'China' },
  { value: 'COL', label: 'Colombia' },
  { value: 'COM', label: 'Comoros' },
  { value: 'COD', label: 'Congo (Democratic Republic)' },
  { value: 'COG', label: 'Congo (Republic)' },
  { value: 'CRI', label: 'Costa Rica' },
  { value: 'CIV', label: "Côte d'Ivoire" },
  { value: 'HRV', label: 'Croatia' },
  { value: 'CUB', label: 'Cuba' },
  { value: 'CUW', label: 'Curaçao' },
  { value: 'CYP', label: 'Cyprus' },
  { value: 'CZE', label: 'Czechia' },
  { value: 'DNK', label: 'Denmark' },
  { value: 'DJI', label: 'Djibouti' },
  { value: 'DMA', label: 'Dominica' },
  { value: 'DOM', label: 'Dominican Republic' },
  { value: 'ECU', label: 'Ecuador' },
  { value: 'EGY', label: 'Egypt' },
  { value: 'SLV', label: 'El Salvador' },
  { value: 'GNQ', label: 'Equatorial Guinea' },
  { value: 'ERI', label: 'Eritrea' },
  { value: 'EST', label: 'Estonia' },
  { value: 'SWZ', label: 'Eswatini' },
  { value: 'ETH', label: 'Ethiopia' },
  { value: 'FJI', label: 'Fiji' },
  { value: 'FIN', label: 'Finland' },
  { value: 'FRA', label: 'France' },
  { value: 'GAB', label: 'Gabon' },
  { value: 'GMB', label: 'Gambia' },
  { value: 'GEO', label: 'Georgia' },
  { value: 'DEU', label: 'Germany' },
  { value: 'GHA', label: 'Ghana' },
  { value: 'GIB', label: 'Gibraltar' },
  { value: 'GRC', label: 'Greece' },
  { value: 'GRD', label: 'Grenada' },
  { value: 'GTM', label: 'Guatemala' },
  { value: 'GGY', label: 'Guernsey' },
  { value: 'GIN', label: 'Guinea' },
  { value: 'GNB', label: 'Guinea-Bissau' },
  { value: 'GUY', label: 'Guyana' },
  { value: 'HTI', label: 'Haiti' },
  { value: 'HND', label: 'Honduras' },
  { value: 'HKG', label: 'Hong Kong' },
  { value: 'HUN', label: 'Hungary' },
  { value: 'ISL', label: 'Iceland' },
  { value: 'IND', label: 'India' },
  { value: 'IDN', label: 'Indonesia' },
  { value: 'IRN', label: 'Iran' },
  { value: 'IRQ', label: 'Iraq' },
  { value: 'IRL', label: 'Ireland' },
  { value: 'IMN', label: 'Isle of Man' },
  { value: 'ISR', label: 'Israel' },
  { value: 'ITA', label: 'Italy' },
  { value: 'JAM', label: 'Jamaica' },
  { value: 'JPN', label: 'Japan' },
  { value: 'JEY', label: 'Jersey' },
  { value: 'JOR', label: 'Jordan' },
  { value: 'KAZ', label: 'Kazakhstan' },
  { value: 'KEN', label: 'Kenya' },
  { value: 'KIR', label: 'Kiribati' },
  { value: 'KWT', label: 'Kuwait' },
  { value: 'KGZ', label: 'Kyrgyzstan' },
  { value: 'LAO', label: 'Laos' },
  { value: 'LVA', label: 'Latvia' },
  { value: 'LBN', label: 'Lebanon' },
  { value: 'LSO', label: 'Lesotho' },
  { value: 'LBR', label: 'Liberia' },
  { value: 'LBY', label: 'Libya' },
  { value: 'LIE', label: 'Liechtenstein' },
  { value: 'LTU', label: 'Lithuania' },
  { value: 'LUX', label: 'Luxembourg' },
  { value: 'MAC', label: 'Macao' },
  { value: 'MDG', label: 'Madagascar' },
  { value: 'MWI', label: 'Malawi' },
  { value: 'MYS', label: 'Malaysia' },
  { value: 'MDV', label: 'Maldives' },
  { value: 'MLI', label: 'Mali' },
  { value: 'MLT', label: 'Malta' },
  { value: 'MHL', label: 'Marshall Islands' },
  { value: 'MRT', label: 'Mauritania' },
  { value: 'MUS', label: 'Mauritius' },
  { value: 'MEX', label: 'Mexico' },
  { value: 'FSM', label: 'Micronesia' },
  { value: 'MDA', label: 'Moldova' },
  { value: 'MCO', label: 'Monaco' },
  { value: 'MNG', label: 'Mongolia' },
  { value: 'MNE', label: 'Montenegro' },
  { value: 'MAR', label: 'Morocco' },
  { value: 'MOZ', label: 'Mozambique' },
  { value: 'MMR', label: 'Myanmar' },
  { value: 'NAM', label: 'Namibia' },
  { value: 'NRU', label: 'Nauru' },
  { value: 'NPL', label: 'Nepal' },
  { value: 'NLD', label: 'Netherlands' },
  { value: 'NZL', label: 'New Zealand' },
  { value: 'NIC', label: 'Nicaragua' },
  { value: 'NER', label: 'Niger' },
  { value: 'NGA', label: 'Nigeria' },
  { value: 'MKD', label: 'North Macedonia' },
  { value: 'NOR', label: 'Norway' },
  { value: 'OMN', label: 'Oman' },
  { value: 'PAK', label: 'Pakistan' },
  { value: 'PLW', label: 'Palau' },
  { value: 'PAN', label: 'Panama' },
  { value: 'PNG', label: 'Papua New Guinea' },
  { value: 'PRY', label: 'Paraguay' },
  { value: 'PER', label: 'Peru' },
  { value: 'PHL', label: 'Philippines' },
  { value: 'POL', label: 'Poland' },
  { value: 'PRT', label: 'Portugal' },
  { value: 'PRI', label: 'Puerto Rico' },
  { value: 'QAT', label: 'Qatar' },
  { value: 'ROU', label: 'Romania' },
  { value: 'RUS', label: 'Russia' },
  { value: 'RWA', label: 'Rwanda' },
  { value: 'KNA', label: 'Saint Kitts and Nevis' },
  { value: 'LCA', label: 'Saint Lucia' },
  { value: 'VCT', label: 'Saint Vincent and the Grenadines' },
  { value: 'WSM', label: 'Samoa' },
  { value: 'SMR', label: 'San Marino' },
  { value: 'STP', label: 'São Tomé and Príncipe' },
  { value: 'SAU', label: 'Saudi Arabia' },
  { value: 'SEN', label: 'Senegal' },
  { value: 'SRB', label: 'Serbia' },
  { value: 'SYC', label: 'Seychelles' },
  { value: 'SLE', label: 'Sierra Leone' },
  { value: 'SGP', label: 'Singapore' },
  { value: 'SXM', label: 'Sint Maarten' },
  { value: 'SVK', label: 'Slovakia' },
  { value: 'SVN', label: 'Slovenia' },
  { value: 'SLB', label: 'Solomon Islands' },
  { value: 'SOM', label: 'Somalia' },
  { value: 'ZAF', label: 'South Africa' },
  { value: 'KOR', label: 'South Korea' },
  { value: 'SSD', label: 'South Sudan' },
  { value: 'ESP', label: 'Spain' },
  { value: 'LKA', label: 'Sri Lanka' },
  { value: 'SDN', label: 'Sudan' },
  { value: 'SUR', label: 'Suriname' },
  { value: 'SWE', label: 'Sweden' },
  { value: 'CHE', label: 'Switzerland' },
  { value: 'SYR', label: 'Syria' },
  { value: 'TWN', label: 'Taiwan' },
  { value: 'TJK', label: 'Tajikistan' },
  { value: 'TZA', label: 'Tanzania' },
  { value: 'THA', label: 'Thailand' },
  { value: 'TLS', label: 'Timor-Leste' },
  { value: 'TGO', label: 'Togo' },
  { value: 'TON', label: 'Tonga' },
  { value: 'TTO', label: 'Trinidad and Tobago' },
  { value: 'TUN', label: 'Tunisia' },
  { value: 'TUR', label: 'Türkiye' },
  { value: 'TKM', label: 'Turkmenistan' },
  { value: 'TCA', label: 'Turks and Caicos Islands' },
  { value: 'TUV', label: 'Tuvalu' },
  { value: 'UGA', label: 'Uganda' },
  { value: 'UKR', label: 'Ukraine' },
  { value: 'ARE', label: 'United Arab Emirates' },
  { value: 'GBR', label: 'United Kingdom' },
  { value: 'URY', label: 'Uruguay' },
  { value: 'UZB', label: 'Uzbekistan' },
  { value: 'VUT', label: 'Vanuatu' },
  { value: 'VEN', label: 'Venezuela' },
  { value: 'VNM', label: 'Vietnam' },
  { value: 'YEM', label: 'Yemen' },
  { value: 'ZMB', label: 'Zambia' },
  { value: 'ZWE', label: 'Zimbabwe' },
]

/**
 * ISO 3166-2:US subdivision codes. Required in practice for a USA company —
 * Sumsub's registry check needs the state to find the filing.
 */
const US_STATES: SelectOption[] = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
]

/**
 * ISO 3166-2:CA subdivision codes, plus `FE` — the code Sumsub's registry
 * check uses for federally incorporated Canadian companies.
 */
const CA_PROVINCES: SelectOption[] = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
  { value: 'FE', label: 'Federal (no province)' },
]

const REGISTRATION_STATUSES: SelectOption[] = [
  { value: 'registered', label: 'Registered' },
  { value: 'exempt', label: 'Exempt' },
  { value: 'not_applicable', label: 'Not applicable' },
]

const RISK_TOLERANCES: SelectOption[] = [
  { value: 'conservative', label: 'Conservative' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'aggressive', label: 'Aggressive' },
]

const AUM_BANDS: SelectOption[] = [
  { value: 'under_10m', label: 'Under $10M' },
  { value: '10m_50m', label: '$10M – $50M' },
  { value: '50m_250m', label: '$50M – $250M' },
  { value: '250m_1b', label: '$250M – $1B' },
  { value: 'over_1b', label: 'Over $1B' },
]

const EMPLOYEE_BANDS: SelectOption[] = [
  { value: '1_10', label: '1 – 10' },
  { value: '11_50', label: '11 – 50' },
  { value: '51_200', label: '51 – 200' },
  { value: '201_1000', label: '201 – 1,000' },
  { value: 'over_1000', label: 'Over 1,000' },
]

/**
 * PHASE 1, block A — who the entity is.
 *
 * 🔴 This section is the DOOR, and its contents are not arbitrary. Every
 * institution field the server can name in a submit-time `missing_fields`
 * list lives here — `legal_form`, `incorporation_country`,
 * `incorporation_state`, `registration_number`, `incorporated_on` — alongside
 * `institution_name`, without which no organization can be created at all.
 * Phase 1 must be submittable on its own, so moving any of these into phase 2
 * would make the phase-1 submit fail with a 400 pointing at a field the user
 * has not been shown.
 *
 * `institution_type` and `registration_status` have no readers anywhere in the
 * codebase, and `core/sumsub/levels.py` explicitly forbids using
 * `institution_type` for verification-level selection (that is `legal_form`'s
 * job). They are kept deliberately, as a product decision.
 */
export const PHASE_ONE_SECTIONS: SectionDef[] = [
  {
    id: 'institution',
    title: 'Institution information',
    fields: [
      {
        name: 'institution_name',
        label: 'Institution name',
        kind: 'text',
        placeholder: 'Acme Capital Partners',
        span: 2,
        help: 'This becomes the name of your organization on SecondLane.',
      },
      { name: 'institution_type', label: 'Institution type', kind: 'select', options: INSTITUTION_TYPES },
      {
        name: 'registration_status',
        label: 'Registration status',
        kind: 'select',
        options: REGISTRATION_STATUSES,
      },
      { name: 'tax_id_ein', label: 'Tax ID / EIN', kind: 'text', placeholder: 'XX-XXXXXXX' },
      {
        name: 'legal_form',
        label: 'Legal form',
        kind: 'select',
        options: LEGAL_FORMS,
        help: 'It cannot be changed once business verification has started.',
      },
      {
        name: 'incorporation_country',
        label: 'Country of incorporation',
        kind: 'select',
        options: INCORPORATION_COUNTRIES,
      },
      {
        name: 'incorporation_state',
        label: 'State of incorporation',
        kind: 'select',
        options: US_STATES,
        showWhen: { field: 'incorporation_country', equalsAny: ['USA'] },
      },
      {
        name: 'incorporation_state',
        label: 'Province of incorporation',
        kind: 'select',
        options: CA_PROVINCES,
        showWhen: { field: 'incorporation_country', equalsAny: ['CAN'] },
      },
      {
        name: 'registration_number',
        label: 'Company registration number',
        kind: 'text',
        placeholder: 'As it appears on the state or national registry',
      },
      { name: 'incorporated_on', label: 'Date of incorporation', kind: 'date' },
    ],
  },
]

/**
 * PHASE 2 — everything that can wait.
 *
 * None of these is required to submit the attestation. Deferring them is the
 * whole point of the redesign: a user gets in after phase 1 and fills these in
 * when they come back to finish verification.
 */
export const PHASE_TWO_SECTIONS: SectionDef[] = [
  {
    id: 'contact',
    title: 'Primary contact',
    fields: [
      { name: 'primary_contact_name', label: 'Contact name', kind: 'text', placeholder: 'Full name' },
      { name: 'primary_contact_title', label: 'Title', kind: 'text', placeholder: 'Job title' },
      {
        name: 'primary_contact_email',
        label: 'Email',
        kind: 'email',
        placeholder: 'name@company.com',
      },
      { name: 'primary_contact_phone', label: 'Phone', kind: 'tel', placeholder: '+1 (555) 555-5555' },
    ],
  },
  {
    id: 'address',
    title: 'Mailing address',
    fields: [
      { name: 'mailing_address_line1', label: 'Address line 1', kind: 'text', span: 2 },
      { name: 'mailing_address_line2', label: 'Address line 2', kind: 'text', span: 2 },
      { name: 'mailing_city', label: 'City', kind: 'text' },
      { name: 'mailing_state', label: 'State / Province', kind: 'text' },
      { name: 'mailing_postal_code', label: 'Postal code', kind: 'text' },
      { name: 'mailing_country', label: 'Country', kind: 'text', placeholder: 'United States' },
    ],
  },
  {
    id: 'regulatory',
    title: 'Regulatory identifiers',
    description: 'Leave blank anything that does not apply to your entity.',
    fields: [
      { name: 'sec_registration_number', label: 'SEC registration number', kind: 'text' },
      { name: 'state_registration', label: 'State registration', kind: 'text' },
      { name: 'finra_crd_number', label: 'FINRA CRD number', kind: 'text' },
      { name: 'other_regulatory_identifiers', label: 'Other identifiers', kind: 'text' },
    ],
  },
  {
    id: 'account',
    title: 'Account profile',
    fields: [
      { name: 'risk_tolerance', label: 'Risk tolerance', kind: 'select', options: RISK_TOLERANCES },
      {
        name: 'assets_under_management',
        label: 'Assets under management',
        kind: 'select',
        options: AUM_BANDS,
      },
      {
        name: 'number_of_employees',
        label: 'Number of employees',
        kind: 'select',
        options: EMPLOYEE_BANDS,
      },
      {
        name: 'investment_objectives',
        label: 'Investment objectives',
        kind: 'textarea',
        span: 2,
        placeholder: 'What is this entity trying to achieve on SecondLane?',
      },
    ],
  },
  {
    id: 'background',
    title: 'Background',
    fields: [
      { name: 'description_of_business', label: 'Description of business', kind: 'textarea', span: 2 },
      { name: 'investment_experience', label: 'Investment experience', kind: 'textarea', span: 2 },
      {
        name: 'previous_broker_dealer_relationships',
        label: 'Previous broker-dealer relationships',
        kind: 'textarea',
        span: 2,
      },
    ],
  },
  {
    id: 'banking',
    title: 'Banking details',
    description:
      'For security, account and routing numbers are never displayed again after you save them — they show as "on file" when you return.',
    fields: [
      { name: 'bank_name', label: 'Bank name', kind: 'text' },
      { name: 'bank_address', label: 'Bank address', kind: 'text' },
      {
        name: 'bank_account_number',
        label: 'Account number',
        kind: 'text',
        writeOnly: true,
        placeholder: 'Enter account number',
      },
      {
        name: 'bank_routing_number',
        label: 'Routing number',
        kind: 'text',
        writeOnly: true,
        placeholder: 'Enter routing number',
      },
    ],
  },
  // The "Authorized signers" section is deliberately GONE. It dates from the
  // mock built before the Sumsub integration existed; Sumsub collects and
  // VERIFIES the entity's signatories (authorizedSignatory / representative /
  // director) as part of the KYB level, and nothing in this codebase reads the
  // six free-text `authorized_signer1/2_*` fields (the attestation signature is
  // a separate record keyed on the submitter's own user_id/email). A companion
  // alocasia MR drops the columns; this UI change must land first or together
  // with it, and the fields' absence from ALL_PROFILE_FIELD_NAMES means the
  // PATCH omits them either way.
]

/**
 * Every profile section, both phases, in order.
 *
 * 🔴 Hydration and `ALL_PROFILE_FIELD_NAMES` walk THIS list, not one phase.
 * A resume has to restore fields from both phases regardless of which one the
 * user is standing in, and the PATCH builder has to know every writable name
 * or it would silently drop the phase it is not looking at.
 */
export const PROFILE_SECTIONS: SectionDef[] = [...PHASE_ONE_SECTIONS, ...PHASE_TWO_SECTIONS]

/**
 * Step 2 — the entity attestation.
 *
 * These booleans live on the institutional profile, not on the attestation row:
 * the attestation owns the lifecycle (status, who signed, when), the profile
 * owns the answers. The server validates them on submit, and every name here
 * appears verbatim in the `missing_fields` list of a 400.
 */
export const QUALIFICATION_FIELDS = [
  'accredited_investor_status',
  'qualified_institutional_buyer',
  'institutional_customer',
] as const

export const COMPLIANCE_FIELDS = [
  'anti_money_laundering_compliance',
  'know_your_customer_compliance',
  'sanctions_compliance',
  'data_protection_compliance',
] as const

export const QUALIFICATION_COPY: Record<
  (typeof QUALIFICATION_FIELDS)[number],
  { label: string; help: string }
> = {
  accredited_investor_status: {
    label: 'Institutional accredited investor',
    help: 'The entity meets the accredited-investor definition in Rule 501(a) of Regulation D.',
  },
  qualified_institutional_buyer: {
    label: 'Qualified institutional buyer (QIB)',
    help: 'The entity owns and invests at least $100M in securities of unaffiliated issuers (Rule 144A).',
  },
  institutional_customer: {
    label: 'Institutional customer',
    help: 'The entity qualifies as an institutional account under FINRA Rule 4512(c).',
  },
}

export const COMPLIANCE_COPY: Record<
  (typeof COMPLIANCE_FIELDS)[number],
  { label: string; help: string }
> = {
  anti_money_laundering_compliance: {
    label: 'Anti-money-laundering (AML) programme',
    help: 'The entity maintains a written AML programme and complies with applicable AML law.',
  },
  know_your_customer_compliance: {
    label: 'Know-your-customer (KYC) procedures',
    help: 'The entity performs customer identification and due diligence on its own clients.',
  },
  sanctions_compliance: {
    label: 'Sanctions screening',
    help: 'The entity screens against OFAC and other applicable sanctions lists, and is not itself a sanctioned party.',
  },
  data_protection_compliance: {
    label: 'Data protection',
    help: 'The entity handles personal data in line with applicable data-protection law.',
  },
}

/**
 * Every profile field the wizard renders, flattened — used for hydration.
 * Deduplicated because `incorporation_state` is declared twice (one select per
 * country whose registry check needs a subdivision; they never render together).
 */
export const ALL_PROFILE_FIELD_NAMES: (keyof InstitutionalProfileFormData)[] = [
  ...new Set<keyof InstitutionalProfileFormData>([
    ...PROFILE_SECTIONS.flatMap(section => section.fields.map(field => field.name)),
    ...QUALIFICATION_FIELDS,
    ...COMPLIANCE_FIELDS,
    'other_certifications',
    // Deliberately absent — mock-phase fields consolidated away once the
    // Sumsub integration landed: `authorized_signer1/2_*` (Sumsub verifies
    // signatories as part of the KYB level; columns dropped in a companion
    // alocasia MR), `years_in_business` (derivable from `incorporated_on`),
    // `account_type` (duplicates `legal_form`, the validated classification
    // that drives level selection), `duns_number` (no Sumsub attribute, no
    // reader), and `referral_source` (never had a UI control). Omitting a key
    // from the PATCH means "unchanged", so any stored value survives resume.
    'marketing_consent',
    'terms_accepted',
  ]),
]

/**
 * Write-only fields, by name.
 *
 * 🔴 An empty value for one of these means "leave what is stored alone", never
 * "clear it" — the API never returns them, so an empty box on resume is the
 * normal state and sending it would wipe a value the user never saw.
 */
export const WRITE_ONLY_FIELDS: (keyof InstitutionalProfileFormData)[] = [
  'bank_account_number',
  'bank_routing_number',
]

/**
 * Fields whose empty string must be sent as `null`, by name.
 *
 * 🔴 The backend VALIDATES these: a non-null `legal_form` outside the seven
 * LEGAL_FORMS values is a 400 `invalid_legal_form`, and `incorporated_on` is a
 * DATE column that cannot parse `''`. Sending the form's empty-string defaults
 * verbatim would fail every autosave from the first keystroke. `null` is the
 * server's "not provided", and sending it explicitly (rather than omitting the
 * key) still lets a cleared value clear — e.g. `incorporation_state` after the
 * incorporation country changes.
 */
export const NULL_WHEN_EMPTY_FIELDS: (keyof InstitutionalProfileFormData)[] = [
  'legal_form',
  'incorporation_country',
  'incorporation_state',
  'registration_number',
  'incorporated_on',
]
